import type { Descendant } from 'slate';

import type { Webcast } from '../../../../generated/graphql-manager';

import { Heading, Paragraph, Tab, Tabs, notification } from '@movingimage-evp/mi-ui-component-library';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { LanguageEditors } from './language-editors';
import { parseContent } from '../../../../components/rich-text-editor';
import {
  State,
  useGetWebcastDescriptionQuery,
  useUpdateWebcastDescriptionMutation,
} from '../../../../generated/graphql-manager';
import { useAbsoluteRoutes } from '../../../../routes';
import { getLanguageKey } from '../../../../utils';
import { SetupPageFooter } from '../../../components/setup-page-footer';
import { useUserPermissions } from '../../../hooks/user-permissions';

import styles from './event-description.module.css';
import managerStyles from '../../../manager.module.css';

export function EventDescriptionPage() {
  const { webcastId = '' } = useParams();
  const { t } = useTranslation();
  const routes = useAbsoluteRoutes();
  const { isEventEditingAllowed } = useUserPermissions();

  const [savingFailed, setSavingFailed] = useState(false);
  const [editorRefreshKeys, setEditorRefreshKeys] = useState([Math.random(), Math.random(), Math.random()]);

  const getWebcastDescriptions = (contents: Webcast['contents']) => {
    setDescriptions(
      contents?.map((content) => ({
        language: content.language,
        preLive: parseContent(content.preLive?.description),
        live: parseContent(content.live?.description),
        postLive: parseContent(content.postLive?.description),
      })) || []
    );

    // RichTextEditor component is not re-rendering when the value prop changes, so we need to force it to do so
    setEditorRefreshKeys([Math.random(), Math.random(), Math.random()]);
  };

  const { data, loading } = useGetWebcastDescriptionQuery({
    variables: { webcastId },
    skip: !webcastId,
    onCompleted(data) {
      getWebcastDescriptions(data?.webcast?.contents);
    },
  });

  const webcast = data?.webcast;
  const archivedWebcast = webcast?.state === State.ARCHIVED;
  const primaryLanguage = webcast?.primaryLang;
  const additionalLanguages = webcast?.additionalLangs || [];
  const languages = [primaryLanguage, ...additionalLanguages]
    .filter((language) => language !== undefined)
    .filter((language) => language.__typename === 'StandardLanguage');

  const [descriptions, setDescriptions] = useState(
    webcast?.contents.map((content) => ({
      language: content.language,
      preLive: parseContent(content.preLive?.description),
      live: parseContent(content.live?.description),
      postLive: parseContent(content.postLive?.description),
    })) || []
  );

  const eventDescription = (language: string, type: 'preLive' | 'live' | 'postLive') => {
    return descriptions.find((description) => description.language === language)?.[type];
  };

  const handleDescriptionChange = (value: Descendant[], language: string, type: 'preLive' | 'live' | 'postLive') => {
    setDescriptions((prevDescriptions) =>
      prevDescriptions.map((description) => {
        if (description.language === language) {
          return {
            ...description,
            [type]: value,
          };
        }

        return description;
      })
    );
  };

  const [updateWebcastDescriptionMutation, { loading: updatingWebcastDescription }] =
    useUpdateWebcastDescriptionMutation();

  const [unsavedChanges, setUnsavedChanges] = useState<
    Record<string, { prelive: boolean; live: boolean; postlive: boolean }>
  >({});

  useEffect(() => {
    if (webcast?.contents && descriptions.length > 0) {
      webcast.contents.forEach((content, idx) => {
        const current = descriptions[idx];
        const language = content.language;
        const preLiveChanged =
          JSON.stringify(parseContent(content.preLive?.description)) !== JSON.stringify(current?.preLive);

        if (preLiveChanged !== unsavedChanges[language]?.prelive) {
          setUnsavedChanges((prev) => ({
            ...prev,
            [language]: {
              ...prev[language],
              prelive: preLiveChanged,
            },
          }));
        }

        const liveChanged = JSON.stringify(parseContent(content.live?.description)) !== JSON.stringify(current?.live);
        if (liveChanged !== unsavedChanges[language]?.live) {
          setUnsavedChanges((prev) => ({
            ...prev,
            [language]: {
              ...prev[language],
              live: liveChanged,
            },
          }));
        }

        const postLiveChanged =
          JSON.stringify(parseContent(content.postLive?.description)) !== JSON.stringify(current?.postLive);
        if (postLiveChanged !== unsavedChanges[language]?.postlive) {
          setUnsavedChanges((prev) => ({
            ...prev,
            [language]: {
              ...prev[language],
              postlive: postLiveChanged,
            },
          }));
        }
      });
    }
  }, [descriptions, unsavedChanges, webcast?.contents]);

  const saveChanges = () => {
    updateWebcastDescriptionMutation({
      variables: {
        input: {
          id: webcastId,
          contents: data?.webcast.contents?.map((content) => ({
            language: content.language,
            preLive: {
              description: JSON.stringify(eventDescription(content.language, 'preLive')),
            },
            live: {
              description: JSON.stringify(eventDescription(content.language, 'live')),
            },
            postLive: {
              description: JSON.stringify(eventDescription(content.language, 'postLive')),
            },
          })),
        },
      },
      optimisticResponse: {
        updateWebcast: {
          __typename: 'UpdateWebcastSuccess',
          webcast: {
            id: webcastId,
            contents: descriptions.map((description) => ({
              language: description.language,
              preLive: {
                description: JSON.stringify(description.preLive),
              },
              live: {
                description: JSON.stringify(description.live),
              },
              postLive: {
                description: JSON.stringify(description.postLive),
              },
            })),
          },
        },
      },
      onCompleted: (data) => {
        if (data.updateWebcast.__typename === 'UpdateWebcastSuccess') {
          getWebcastDescriptions(data.updateWebcast.webcast.contents);
        } else {
          notification(t('manager.webcastSetup.toast.saveError'), 'negative');
        }

        setSavingFailed(data.updateWebcast.__typename !== 'UpdateWebcastSuccess');
        setUnsavedChanges({});
      },
      onError: () => {
        notification(t('manager.webcastSetup.toast.saveError'), 'negative');
      },
    });
  };

  const handleTabChange = useCallback(() => {
    setEditorRefreshKeys([Math.random(), Math.random(), Math.random()]);
  }, []);

  return (
    <main className={managerStyles.main} data-testid="event-description-page">
      <Heading className={managerStyles.grayText}>{t('manager.webcastSetup.eventDescription.title')}</Heading>
      <Paragraph>{t('manager.webcastSetup.eventDescription.heading')}</Paragraph>

      {!loading && (
        <>
          {primaryLanguage && languages.length === 1 && (
            <LanguageEditors
              language={primaryLanguage.code}
              disabled={archivedWebcast}
              preLiveDescription={eventDescription(primaryLanguage.code, 'preLive')}
              preLiveDescriptionChanged={unsavedChanges[primaryLanguage.code]?.prelive}
              liveDescription={eventDescription(primaryLanguage.code, 'live')}
              liveDescriptionChanged={unsavedChanges[primaryLanguage.code]?.live}
              postLiveDescription={eventDescription(primaryLanguage.code, 'postLive')}
              postLiveDescriptionChanged={unsavedChanges[primaryLanguage.code]?.postlive}
              editorRefreshKeys={editorRefreshKeys}
              onDescriptionChange={handleDescriptionChange}
            />
          )}

          {languages.length > 1 && (
            <Tabs compact={languages.length <= 4} onSelectTab={handleTabChange}>
              {languages.map((language) => {
                const isPrimaryLanguage = language === primaryLanguage;

                const translatedLanguage = t(
                  `manager.webcastSetup.language.supportedLanguages.${getLanguageKey(language.code)}`
                );

                const label = isPrimaryLanguage
                  ? t('manager.webcastSetup.eventDescription.primaryLanguage', { primaryLanguage: translatedLanguage })
                  : translatedLanguage;

                const unsavedChangesForLanguage =
                  unsavedChanges[language.code]?.prelive ||
                  unsavedChanges[language.code]?.live ||
                  unsavedChanges[language.code]?.postlive;

                return (
                  <Tab
                    label={`${label}${unsavedChangesForLanguage ? ' *' : ''}`}
                    key={language.code}
                    data-testid={`event-description-tab-${language.code}`}
                  >
                    <LanguageEditors
                      language={language.code}
                      disabled={archivedWebcast}
                      preLiveDescription={eventDescription(language.code, 'preLive')}
                      preLiveDescriptionChanged={unsavedChanges[language.code]?.prelive}
                      liveDescription={eventDescription(language.code, 'live')}
                      liveDescriptionChanged={unsavedChanges[language.code]?.live}
                      postLiveDescription={eventDescription(language.code, 'postLive')}
                      postLiveDescriptionChanged={unsavedChanges[language.code]?.postlive}
                      editorRefreshKeys={editorRefreshKeys}
                      onDescriptionChange={handleDescriptionChange}
                      className={styles.multilanguageWrapper}
                    />
                  </Tab>
                );
              })}
            </Tabs>
          )}

          <SetupPageFooter
            nextLabel={t('manager.webcastSetup.eventDescription.nextStep.label')}
            route={routes.webcastSetup_media}
            showSaveButton
            saveButtonDisabled={!isEventEditingAllowed || archivedWebcast}
            saving={updatingWebcastDescription}
            savingFailed={savingFailed}
            onSaveButtonClick={saveChanges}
          />
        </>
      )}
    </main>
  );
}
